<h2 class="section-title">{{ 'components.settingsForm.tagSection.form.title' | translate }}</h2>
<form [formGroup]="tagForm" (ngSubmit)="onSubmit()">
    <div class="card form-grid">
        <div class="form-field">
            <label for="tagNameInput" class="form-field__label form-field__label--required">
                {{ 'components.settingsForm.tagSection.form.name' | translate }}
            </label>
            <input
                id="tagNameInput"
                type="text"
                pInputText
                formControlName="name"
                [placeholder]="'components.settingsForm.tagSection.form.placeholder.name' | translate" />
            @if (tagForm.controls.name.errors?.['required'] && tagForm.controls.name.touched) {
                <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
            }
        </div>
        <div class="form-field">
            <label for="tagDescriptionInput" class="form-field__label">
                {{ 'components.settingsForm.tagSection.form.description' | translate }}
            </label>
            <textarea
                id="tagDescriptionInput"
                rows="5"
                pTextarea
                formControlName="description"
                [placeholder]="
                    'components.settingsForm.tagSection.form.placeholder.description' | translate
                "></textarea>
        </div>
        <!-- Parent or child tag -->
        <div class="form-choices">
            @for (tag of tagsType; track tag) {
                <div class="form-choice">
                    <p-radioButton
                        [inputId]="'tagType' + tag.key"
                        [value]="tag.key"
                        formControlName="subTag"></p-radioButton>
                    <label [for]="'tagType' + tag.key">{{ tag.name }}</label>
                    <!-- nosemgrep: java.lang.security.audit.xss.jsf.autoescape-disabled.autoescape-disabled -->
                    <span
                        class="material-symbols-outlined icon-sm cursor-help"
                        [pTooltip]="'components.settingsForm.tagSection.tooltip.' + tag.key | translate"
                        [escape]="false"
                        >info</span
                    >
                </div>
            }
        </div>
        @if (subTag === 'P') {
            <div class="form-field">
                <label for="childTagsInput" class="form-field__label">
                    {{ 'components.settingsForm.tagSection.form.childsTags' | translate }}
                </label>
                <p-multiSelect
                    inputId="childTagsInput"
                    formControlName="childsTag"
                    [options]="currentPossibleChildTags"
                    optionLabel="label"
                    [placeholder]="'components.settingsForm.tagSection.form.placeholder.parentTag' | translate"
                    display="chip"
                    (onChange)="onChildTagChange($event.value)"
                    [filterPlaceHolder]="'common.search' | translate">
                    <ng-template pTemplate="item" let-tag>
                        {{ tag.tag.name }}
                    </ng-template>
                    <ng-template pTemplate="selectedItems" let-selectedItems>
                        @if (selectedItems?.length > 0) {
                            <div class="chip-list">
                                @for (item of selectedItems; track item) {
                                    <p-chip
                                        [label]="item.tag.name"
                                        [removable]="true"
                                        (onRemove)="removeSelectedItem(item, 'childsTag'); $event.stopPropagation()" />
                                }
                            </div>
                        }
                    </ng-template>
                </p-multiSelect>
                <small class="form-field__help flex align-items-center gap-2">
                    <span class="material-symbols-outlined icon-sm" aria-hidden="true">warning</span>
                    {{ 'components.settingsForm.tagSection.form.parentLinkedEntitiesWarning' | translate }}
                </small>
            </div>
            <div class="form-field">
                <label for="linkedEntitiesInput" class="form-field__label">
                    {{ 'components.settingsForm.tagSection.form.linkedEntities' | translate }}
                    <span
                        class="material-symbols-outlined icon-sm align-middle cursor-help"
                        [pTooltip]="'components.settingsForm.tagSection.form.infotips.linkedEntities' | translate"
                        >info</span
                    >
                </label>
                <p-multiSelect
                    inputId="linkedEntitiesInput"
                    formControlName="linkedEntities"
                    [options]="entitiesDropdownEntries"
                    optionLabel="label"
                    [placeholder]="'components.settingsForm.tagSection.form.placeholder.allEntities' | translate"
                    display="chip"
                    optionDisabled="isDisabled"
                    appendTo="body"
                    [filterPlaceHolder]="'common.search' | translate">
                    <ng-template pTemplate="option" let-entity>
                        {{ entity.entity.name }}
                    </ng-template>
                    <ng-template pTemplate="selectedItems" let-selectedItems>
                        @if (selectedItems?.length > 0) {
                            <div class="chip-list">
                                <!-- Entities imposed by a child tag cannot be removed. -->
                                @for (item of selectedItems; track item) {
                                    <p-chip
                                        [label]="item.entity.name"
                                        [removable]="!item.isDisabled"
                                        (onRemove)="
                                            removeSelectedItem(item, 'linkedEntities'); $event.stopPropagation()
                                        " />
                                }
                            </div>
                        }
                    </ng-template>
                </p-multiSelect>
            </div>
        }
        @if (subTag === 'C') {
            <div class="form-field">
                <label for="parentTagInput" class="form-field__label form-field__label--required">
                    {{ 'components.settingsForm.tagSection.form.parentTag' | translate }}
                </label>
                <p-select
                    inputId="parentTagInput"
                    formControlName="parentTag"
                    [options]="possibleParentTags"
                    [placeholder]="'components.settingsForm.tagSection.form.placeholder.childTag' | translate"
                    (onChange)="onParentTagChange($event.value)">
                    <ng-template pTemplate="item" let-tag>
                        {{ tag.tag.name }}
                    </ng-template>
                    <ng-template pTemplate="selectedItem" let-selectedTag>
                        {{ selectedTag.tag.name }}
                    </ng-template>
                </p-select>
                @if (!tagForm.controls.parentTag.value && tagForm.controls.parentTag.touched) {
                    <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                }
            </div>
            <div class="form-field">
                <label for="linkedParentEntitiesInput" class="form-field__label">
                    {{ 'components.settingsForm.tagSection.form.parentLinkedEntities' | translate }}
                </label>
                <p-multiSelect
                    inputId="linkedParentEntitiesInput"
                    formControlName="linkedParentEntities"
                    [options]="possibleEntities"
                    [placeholder]="'components.settingsForm.tagSection.form.placeholder.allEntities' | translate"
                    optionLabel="label"
                    display="chip"
                    [filterPlaceHolder]="'common.search' | translate">
                    <ng-template pTemplate="option" let-entity>
                        {{ entity.name }}
                    </ng-template>
                    <ng-template pTemplate="selectedItems" let-selectedItems>
                        @if (selectedItems?.length > 0) {
                            <div class="chip-list">
                                @for (item of selectedItems; track item) {
                                    <p-chip
                                        [label]="item.name"
                                        [removable]="true"
                                        (onRemove)="
                                            removeSelectedItem(item, 'linkedParentEntities'); $event.stopPropagation()
                                        " />
                                }
                            </div>
                        }
                    </ng-template>
                </p-multiSelect>
                <small class="form-field__help flex align-items-center gap-2">
                    <span class="material-symbols-outlined icon-sm" aria-hidden="true">warning</span>
                    {{ 'components.settingsForm.tagSection.form.childsTagsWarning' | translate }}
                </small>
            </div>
        }
    </div>
    <div class="form-actions">
        @if (selectedTag) {
            <!-- The wrapper carries the tooltip: a disabled button does not report hovers. -->
            <span
                class="form-actions__start"
                [pTooltip]="
                    (selectedTag.childTags?.length ?? 0 > 0)
                        ? ('components.settingsForm.tagSection.tooltip.remove' | translate)
                        : ''
                ">
                <p-button
                    severity="danger"
                    [text]="true"
                    icon="ms ms-delete"
                    [label]="'components.settingsForm.button.remove' | translate"
                    [disabled]="(selectedTag.childTags?.length ?? 0) > 0"
                    (onClick)="onDelete()" />
            </span>
        }
        <p-button severity="secondary" [label]="'settings.users.button.cancel' | translate" (onClick)="onCancel()" />
        <p-button
            type="submit"
            [label]="
                selectedTag
                    ? ('components.settingsForm.button.save' | translate)
                    : ('components.settingsForm.button.create' | translate)
            " />
    </div>
</form>
<p-confirmDialog />
